import { Button, Flex, theme } from 'antd';
import { useClient, useMutation } from 'jsonapi-react';
import { X } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useReducer, useState } from 'react';
import { catchError, concatMap, EMPTY, from, map, of, switchMap, tap, throwError } from 'rxjs';
import { InvoiceExpandedRow } from './InvoiceExpandedRow';
import InvoicesFilter from './invoices.filter';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  getRequestParamsForGetCustomerMonthInvoicesRequest,
} from './invoices.helper';
import { type InvoicesPageState } from './types';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import LoadingSpin from '../../../components/loading-spin';
import SyncSuspense from '../../../components/sync-suspense';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { type CustomerMonthInvoiceDto } from '../../../dto/billing/CustomerMonthInvoiceDto';
import { type ResponseDto } from '../../../dto/ResponseDto';
import { type CustomerMonthInvoice, type Invoice, TableExport } from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useNotification } from '../../../hooks/use-notification';
import { useScreenWidth } from '../../../hooks/use-screen-width';
import useSecuredApiV2 from '../../../hooks/use-secured-api-v2';
import { CUSTOMER_MONTH_INVOICE_URL } from '../../../utils/endpoints';
import { LocalDate } from '../../../utils/local-date';
import utils from '../../../utils/utils';

const reducer = (
  state: Readonly<InvoicesPageState>,
  action: Readonly<{
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?: InvoicesPageState['filters'] | InvoicesPageState['pagination'];
  }>
): InvoicesPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as InvoicesPageState['filters'],
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as InvoicesPageState['pagination'],
      };
    default:
      return {
        ...state,
      };
  }
};

const InvoicesPage = (): ReactNode => {
  const notification = useNotification();
  const isScreenLG = useScreenWidth('lg');
  const { token } = theme.useToken();
  const [state, setState] = useReducer(reducer, {
    filters: {},
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [getCustomerMonthInvoicesResponse, setCustomerMonthInvoices] = useState<
    ResponseDto<CustomerMonthInvoiceDto[]>
  >([] as unknown as ResponseDto<CustomerMonthInvoiceDto[]>);
  const [debouncedState] = useDebouncedState({ state, time: 300 });
  const [invoiceToView, setInvoiceToView] = useState<string>();
  const apiV2 = useSecuredApiV2();
  const [createExport] = useMutation(['tableExport']);
  useEffect(() => {
    if (!debouncedState.isDebouncing) {
      getCustomerMonthInvoices();
    }
  }, [debouncedState]);

  const getCustomerMonthInvoices = (): void => {
    setIsLoading(true);
    apiV2
      .get(
        CUSTOMER_MONTH_INVOICE_URL,
        getRequestParamsForGetCustomerMonthInvoicesRequest(state),
        {}
      )
      .pipe(
        tap((response) => {
          setCustomerMonthInvoices(response);
          setIsLoading(false);
        })
      )
      .subscribe();
  };

  const columns: DataTableProps<CustomerMonthInvoiceDto>['columns'] = [
    {
      title: 'Customer',
      dataIndex: 'customer',
      render: (value: CustomerMonthInvoice['customer']) => value.name,
      width: '18%',
    },
    {
      title: 'Bill Month',
      dataIndex: 'startDate',
      render: (value: Invoice['startDate']) => {
        return new LocalDate(value).format('MMM YYYY');
      },
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPriceWithTaxes',
      render: (_, record: CustomerMonthInvoiceDto) =>
        utils.currencyFormat(record.totalPriceWithTaxes),
    },
  ];

  const fetchInvoice = useCallback((id: Invoice['id'], callback: (url: string) => void): void => {
    const entity = 'invoice';
    const filterName = 'id';
    const tableExport = new TableExport(
      undefined,
      `/${entity}?fields[${entity}]=&filter=${filterName}==${id}`,
      'PDF'
    );
    from(createExport(tableExport))
      .pipe(
        concatMap((res) =>
          res.error
            ? throwError(() => new Error(res.error?.detail))
            : of(Object.assign(new TableExport(), res.data))
        ),
        concatMap((res) =>
          res.result?.message ? throwError(() => new Error(res.result!.message)) : of(res)
        ),
        concatMap((sheet) =>
          sheet.result!.recordCount
            ? from(fetch(sheet.result!.url)).pipe(
                switchMap((res) => from(res.blob())),
                map((blob) => blob.slice(0, blob.size, 'application/pdf'))
              )
            : of(null)
        ),
        tap((blob) => {
          callback(blob ? URL.createObjectURL(blob) : '');
        }),
        catchError((error: Error) => {
          const [description, title] = error?.message.split('\n');
          notification.error({
            description: description ?? 'No invoices available',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
  }, []);

  const displayInvoice = useCallback((invoiceId: Invoice['id']) => {
    setInvoiceToView('');
    fetchInvoice(invoiceId, setInvoiceToView);
  }, []);

  return (
    <Flex gap="middle" vertical>
      <DataTable<CustomerMonthInvoiceDto>
        title="Invoices"
        description="List of invoices"
        filtersNode={
          <InvoicesFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        currentPage={state.pagination.current}
        loading={isLoading}
        dataList={getCustomerMonthInvoicesResponse?.data ?? []}
        onChange={setState}
        columns={columns}
        totalRecords={getCustomerMonthInvoicesResponse.pagination?.totalRecords}
        expandable={{
          expandedRowRender: (
            record: CustomerMonthInvoiceDto,
            index: number,
            indent: number,
            expanded: boolean
          ) => {
            return expanded ? (
              <InvoiceExpandedRow
                customerMonthInvoiceId={record.id}
                filters={state.filters}
                fetchInvoice={fetchInvoice}
                displayInvoice={displayInvoice}
                apiClient={useClient()}
                onUpdate={() => {
                  getCustomerMonthInvoices();
                }}
              />
            ) : null;
          },
        }}
      />

      <SyncSuspense condition={invoiceToView}>
        <ExtraContent>
          <SyncSuspense fallback={<LoadingSpin />} condition={invoiceToView !== ''}>
            <Flex gap="middle" vertical style={{ alignItems: 'flex-end' }}>
              {isScreenLG && (
                <Button
                  type="text"
                  icon={<X />}
                  style={{ marginBlockStart: token.marginSM }}
                  onClick={() => {
                    setInvoiceToView('');
                  }}
                />
              )}
              <iframe
                src={invoiceToView}
                style={{ border: 0, height: 'auto', aspectRatio: '8.5 / 11' }}
                width="100%"
              />
            </Flex>
          </SyncSuspense>
        </ExtraContent>
      </SyncSuspense>
    </Flex>
  );
};

export default InvoicesPage;
